import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import UpgradeConfirmModal from './UpgradeConfirmModal';

const UpgradeOptionsTab = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      description: 'Perfect for getting started',
      monthlyPrice: 0,
      yearlyPrice: 0,
      color: 'muted',
      popular: false,
      current: false,
      features: [
        { name: 'Business Model Canvas', included: true },
        { name: 'Basic Dashboard', included: true },
        { name: 'Report Downloads', value: '5/month' },
        { name: 'AI Insights', value: '100/month' },
        { name: 'Team Members', value: '1' },
        { name: 'Email Support', included: true },
        { name: 'Predictive Analysis', included: false },
        { name: 'Goal Intelligence', included: false }
      ]
    },
    {
      id: 'growth',
      name: 'Growth',
      description: 'For growing businesses',
      monthlyPrice: 2999,
      yearlyPrice: 28790,
      color: 'primary',
      popular: true,
      current: true,
      features: [
        { name: 'Everything in Free', included: true },
        { name: 'Predictive Analysis', included: true },
        { name: 'Goal Intelligence', included: true },
        { name: 'Advanced Reports', value: '50/month' },
        { name: 'AI Insights', value: '1,000/month' },
        { name: 'Team Members', value: '10' },
        { name: 'Priority Support', included: true },
        { name: 'Custom Integrations', included: false }
      ]
    },
    {
      id: 'scale',
      name: 'Scale',
      description: 'For scaling operations',
      monthlyPrice: 5999,
      yearlyPrice: 57590,
      color: 'secondary',
      popular: false,
      current: false,
      features: [
        { name: 'Everything in Growth', included: true },
        { name: 'Unlimited Reports', included: true },
        { name: 'Unlimited AI Insights', included: true },
        { name: 'Team Members', value: '50' },
        { name: 'Custom Integrations', included: true },
        { name: 'Dedicated Support', included: true },
        { name: 'White-label Options', included: true },
        { name: 'API Access', included: true }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'For large organizations',
      monthlyPrice: null,
      yearlyPrice: null,
      color: 'accent',
      popular: false,
      current: false,
      features: [
        { name: 'Everything in Scale', included: true },
        { name: 'Unlimited Everything', included: true },
        { name: 'Custom Development', included: true },
        { name: 'On-premise Deployment', included: true },
        { name: 'SLA Guarantee', included: true },
        { name: 'Dedicated Account Manager', included: true },
        { name: 'Custom Training', included: true },
        { name: 'Advanced Security', included: true }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: { bg: 'bg-primary', text: 'text-primary', border: 'border-primary', gradient: 'from-primary/20 to-primary/5' },
      secondary: { bg: 'bg-secondary', text: 'text-secondary', border: 'border-secondary', gradient: 'from-secondary/20 to-secondary/5' },
      accent: { bg: 'bg-accent', text: 'text-accent', border: 'border-accent', gradient: 'from-accent/20 to-accent/5' },
      muted: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', gradient: 'from-muted/20 to-muted/5' }
    };
    return colors?.[color] || colors?.primary;
  };

  const calculateSavings = (monthly, yearly) => {
    if (!monthly || !yearly) return 0;
    const annualMonthly = monthly * 12;
    return Math.round(((annualMonthly - yearly) / annualMonthly) * 100);
  };

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setIsUpgradeModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Billing Cycle Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-3 bg-card border border-border rounded-lg p-1.5 elevation-sm">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium font-body transition-smooth ${
              billingCycle === 'monthly' ?'bg-primary text-primary-foreground elevation-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-md text-sm font-medium font-body transition-smooth ${
              billingCycle === 'yearly' ?'bg-primary text-primary-foreground elevation-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly
            <span className="ml-2 px-2 py-0.5 bg-success/20 text-success text-xs rounded-full">
              Save 20%
            </span>
          </button>
        </div>
      </div>
      {/* Plan Comparison Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        {plans?.map((plan) => {
          const colorClasses = getColorClasses(plan?.color);
          const price = billingCycle === 'monthly' ? plan?.monthlyPrice : plan?.yearlyPrice;
          const savings = calculateSavings(plan?.monthlyPrice, plan?.yearlyPrice);

          return (
            <div
              key={plan?.id}
              className={`relative bg-gradient-to-br ${colorClasses?.gradient} border-2 rounded-lg p-4 lg:p-6 transition-smooth hover:elevation-lg ${
                plan?.current ? colorClasses?.border : 'border-border'
              }`}
            >
              {plan?.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className={`px-3 py-1 ${colorClasses?.bg} text-white text-xs font-semibold font-caption rounded-full elevation-sm whitespace-nowrap`}>
                    Most Popular
                  </div>
                </div>
              )}
              {plan?.current && (
                <div className="absolute -top-3 right-4">
                  <div className="px-3 py-1 bg-success text-white text-xs font-semibold font-caption rounded-full elevation-sm whitespace-nowrap">
                    Current Plan
                  </div>
                </div>
              )}

              <div className="text-center mb-4 lg:mb-6">
                <h3 className="text-lg lg:text-xl font-bold text-foreground font-heading mb-2">
                  {plan?.name}
                </h3>
                <p className="text-xs lg:text-sm text-muted-foreground font-caption mb-3 lg:mb-4">
                  {plan?.description}
                </p>
                {price !== null ? (
                  <div>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-2xl lg:text-3xl font-bold text-foreground font-heading">
                        ₹{price?.toLocaleString()}
                      </span>
                      <span className="text-xs lg:text-sm text-muted-foreground font-caption">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && savings > 0 && (
                      <p className="text-xs text-success font-caption mt-1">
                        Save {savings}% with annual billing
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-xl lg:text-2xl font-bold text-foreground font-heading">
                    Custom Pricing
                  </div>
                )}
              </div>

              <div className="space-y-2 lg:space-y-3 mb-4 lg:mb-6">
                {plan?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Icon
                      name={feature?.included || feature?.value ? 'CheckCircle' : 'XCircle'}
                      size={16}
                      color={feature?.included || feature?.value ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span className={`text-xs lg:text-sm font-body break-words ${
                      feature?.included || feature?.value ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {feature?.name}
                      {feature?.value && (
                        <span className="ml-1 text-xs text-muted-foreground font-caption">
                          ({feature?.value})
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              {plan?.current ? (
                <Button variant="outline" size="default" fullWidth disabled>
                  Current Plan
                </Button>
              ) : plan?.monthlyPrice === null ? (
                <Button variant="primary" size="default" fullWidth iconName="MessageCircle" iconPosition="left">
                  Contact Sales
                </Button>
              ) : plan?.id === 'free' ? (
                <Button variant="destructive-outline" size="default" fullWidth iconName="ArrowDown" iconPosition="left">
                  Downgrade
                </Button>
              ) : (
                <Button 
                  variant="primary" 
                  size="default" 
                  fullWidth 
                  iconName="ArrowUp" 
                  iconPosition="left"
                  onClick={() => handleUpgrade(plan)}
                >
                  Upgrade Now
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {/* Feature Comparison Table */}
      <div className="bg-card border border-border rounded-lg p-4 lg:p-6 elevation-sm">
        <div className="flex items-center gap-3 mb-4 lg:mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="List" size={20} color="var(--color-primary)" />
          </div>
          <h3 className="text-base lg:text-lg font-semibold text-foreground font-heading">
            Detailed Feature Comparison
          </h3>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-foreground font-body">
                  Feature
                </th>
                {plans?.map((plan) => (
                  <th key={plan?.id} className="text-center px-4 py-3 text-sm font-semibold text-foreground font-body">
                    {plan?.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                'Business Model Canvas',
                'Predictive Analysis',
                'Goal Intelligence',
                'Report Downloads',
                'AI Insights',
                'Team Members',
                'Custom Integrations',
                'Priority Support'
              ]?.map((feature, index) => (
                <tr key={index} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-4 py-3 text-sm text-foreground font-body">
                    {feature}
                  </td>
                  {plans?.map((plan) => {
                    const planFeature = plan?.features?.find(f => f?.name?.includes(feature?.split(' ')?.[0]));
                    return (
                      <td key={plan?.id} className="text-center px-4 py-3">
                        {planFeature?.included ? (
                          <Icon name="CheckCircle" size={18} color="var(--color-success)" className="mx-auto" />
                        ) : planFeature?.value ? (
                          <span className="text-sm text-foreground font-body">{planFeature?.value}</span>
                        ) : (
                          <Icon name="Minus" size={18} color="var(--color-muted-foreground)" className="mx-auto" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Accordion View */}
        <div className="lg:hidden space-y-3">
          {[
            'Business Model Canvas',
            'Predictive Analysis',
            'Goal Intelligence',
            'Report Downloads',
            'AI Insights',
            'Team Members',
            'Custom Integrations',
            'Priority Support'
          ]?.map((feature, index) => (
            <div key={index} className="bg-muted/30 border border-border rounded-lg p-3">
              <p className="text-sm font-semibold text-foreground font-body mb-3">
                {feature}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {plans?.map((plan) => {
                  const planFeature = plan?.features?.find(f => f?.name?.includes(feature?.split(' ')?.[0]));
                  return (
                    <div key={plan?.id} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-caption">
                        {plan?.name}:
                      </span>
                      {planFeature?.included ? (
                        <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                      ) : planFeature?.value ? (
                        <span className="text-xs text-foreground font-body">{planFeature?.value}</span>
                      ) : (
                        <Icon name="Minus" size={14} color="var(--color-muted-foreground)" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <UpgradeConfirmModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        plan={selectedPlan}
        billingCycle={billingCycle}
      />
    </div>
  );
};

export default UpgradeOptionsTab;